import { FirebaseError } from 'firebase/app';
import {
  EmailAuthProvider,
  TotpMultiFactorGenerator,
  multiFactor,
  reauthenticateWithCredential,
  type TotpSecret,
  type User,
} from 'firebase/auth';
import QRCode from 'qrcode';
import { useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { AnalyticsSetting } from '../components/AnalyticsConsent';
import { Field } from '../components/Field';
import { shopName } from '../config';
import { formatDateTime } from '../lib/dates';
import { errorMessage } from '../lib/errors';

export function AccountPage() {
  const { state } = useAuth();
  if (state.status !== 'signedIn') return null;
  return (
    <section>
      <h1>Account</h1>
      <div className="card">
        <dl className="details">
          <dt>Email</dt>
          <dd>{state.user.email}</dd>
          <dt>Role</dt>
          <dd>{state.role ?? 'none'}</dd>
        </dl>
      </div>
      <TwoStepVerification user={state.user} />
      <AnalyticsSetting />
    </section>
  );
}

type Step = 'idle' | 'reauth' | 'scan';

function TwoStepVerification({ user }: { user: User }) {
  const [factors, setFactors] = useState(() => [...multiFactor(user).enrolledFactors]);
  const totp = factors.find((f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID);

  const [step, setStep] = useState<Step>('idle');
  const [secret, setSecret] = useState<TotpSecret | null>(null);
  const [qr, setQr] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  async function start() {
    setError('');
    setBusy(true);
    try {
      const session = await multiFactor(user).getSession();
      const s = await TotpMultiFactorGenerator.generateSecret(session);
      setQr(await QRCode.toDataURL(s.generateQrCodeUrl(user.email ?? undefined, shopName), { margin: 1, width: 200 }));
      setSecret(s);
      setStep('scan');
    } catch (err) {
      if (err instanceof FirebaseError && err.code === 'auth/requires-recent-login') setStep('reauth');
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function reauth(e: FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email ?? '', password));
      setPassword('');
      setStep('idle');
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
      return;
    }
    setBusy(false);
    await start();
  }

  async function verify(e: FormEvent) {
    e.preventDefault();
    if (!secret) return;
    setError('');
    setBusy(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code.replace(/\s/g, ''));
      await multiFactor(user).enroll(assertion, 'Authenticator app');
      setFactors([...multiFactor(user).enrolledFactors]);
      setSecret(null);
      setQr('');
      setCode('');
      setStep('idle');
      setMessage('Two-step verification is on. You will need a code from the app each time you sign in.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>Two-step verification</h2>
      {message && (
        <p className="alert alert--success" role="status">
          {message}
        </p>
      )}
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      {totp ? (
        <p>
          On: authenticator app added {formatDateTime(new Date(totp.enrollmentTime))}. Lost the phone? Ask an
          admin to reset it.
        </p>
      ) : step === 'idle' ? (
        <>
          <p>Protect your account with a code from an authenticator app, such as Google Authenticator.</p>
          <button type="button" className="btn btn--primary" onClick={() => void start()} disabled={busy}>
            {busy ? 'Starting…' : 'Set up authenticator app'}
          </button>
        </>
      ) : step === 'reauth' ? (
        <form onSubmit={reauth} noValidate className="narrow">
          <p>For security, enter your password again.</p>
          <Field
            label="Password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="actions">
            <button type="submit" className="btn btn--primary" disabled={busy || !password}>
              Continue
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setStep('idle')}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={verify} noValidate className="narrow">
          <ol className="steps">
            <li>Scan this code with your authenticator app.</li>
            <li>Enter the 6-digit code it shows.</li>
          </ol>
          {qr && <img className="qr" src={qr} alt="QR code for your authenticator app" width={200} height={200} />}
          {secret && (
            <p className="muted">
              Can't scan? Enter this key: <code className="secret">{secret.secretKey}</code>
            </p>
          )}
          <Field
            label="Code"
            name="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <div className="actions">
            <button type="submit" className="btn btn--primary" disabled={busy || code.trim().length < 6}>
              {busy ? 'Checking…' : 'Turn on'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setStep('idle');
                setSecret(null);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
